// Quote Basket — standalone, always-on "quote cart" store.
//
// The headline gap this closes: a storefront customer browsing
// product-to-product could never accumulate items into ONE quote —
// nothing survived page navigation. The QuoteBuilder's own draft
// persistence (pe:embed:draft:<slug> in index.ts) is COUPLED to a
// mounted form instance and only accumulates while the drawer is open.
// The basket is decoupled: it persists on its own key, with no drawer
// mounted, and the floating indicator + basket drawer read from it.
//
// STRICT SEPARATION (ratified, docs/QUOTE_BASKET.md §6):
//   - Own localStorage key `pe:basket:<slug>` — NEVER the form-draft
//     key. Builder/wizard draft resume must stay byte-behavior intact;
//     the basket is a separate key + controller that the persist layer
//     in index.ts never reads or writes.
//   - Separate from the Shopify cart — the basket is a quote-request
//     staging area, not a checkout cart.
//   - dedup-by-variant bumps qty (a 2nd add of the same variant adds to
//     the existing line's quantity rather than pushing a duplicate).
//   - Per-line `properties` (custom options) carried from day one.
//
// Design mirrors product-button.ts / floating-widgets.ts: pure-ish core
// with every browser touchpoint (localStorage, storage events) injected
// so the node-only vitest (no jsdom) can drive it with fakes, and every
// storage read/write wrapped so Safari private mode / quota / SSR
// silently degrade to in-memory behavior instead of throwing.

export interface BasketLineProperty {
  name: string;
  value: string;
}

/** One line in the quote basket. Superset of index.ts's CartItem shape
 *  (kept structurally compatible so basket items flow straight into the
 *  form's prefill.lineItems on review-and-submit) PLUS per-line custom
 *  `properties`. */
export interface BasketItem {
  variantId: string;
  productId?: string;
  title: string;
  variantTitle?: string;
  sku?: string;
  qty: number;
  price?: string;
  vendor?: string;
  imageUrl?: string | null;
  /** Inventory awareness — carried so the form's OOS / backorder /
   *  discontinued badges + submit gate survive a round-trip through the
   *  basket store (the unified item source of truth). All optional;
   *  absent on minimal / cart-sync lines, in which case no badge renders
   *  (byte-identical to a pre-inventory line). */
  availableForSale?: boolean;
  inventoryPolicy?: "DENY" | "CONTINUE";
  inventoryQuantity?: number | null;
  discontinued?: boolean;
  /** Custom line-item options (Shopify `properties[...]` convention).
   *  Shared JSON contract with the server route lane:
   *  Array<{name: string, value: string}>. */
  properties?: BasketLineProperty[];
}

interface BasketData {
  version: number;
  updatedAt: string;
  items: BasketItem[];
}

/** Minimal localStorage surface the store touches — lets tests inject a
 *  plain Map-backed fake without jsdom. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface BasketStoreDeps {
  /** Shop slug — the key is `pe:basket:<slug>`. */
  slug?: string;
  /** localStorage or a fake. `undefined` → autodetect window.localStorage;
   *  `null` → force in-memory only (used to simulate a storage-less env). */
  storage?: StorageLike | null;
  /** Clock — injectable so TTL tests are deterministic. */
  now?: () => number;
  /** Register a cross-tab change listener; returns an unsubscribe fn.
   *  Default wires window's `storage` event. The handler receives the
   *  changed key (null on storage.clear()) so we can ignore unrelated
   *  keys cheaply. */
  onStorage?: (handler: (key: string | null) => void) => () => void;
}

export const BASKET_VERSION = 1;
export const BASKET_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — mirrors the form-draft TTL

/** The localStorage key for a shop's basket. Deliberately DISTINCT from
 *  the form-draft key (`pe:embed:draft:<slug>`) so the two never collide
 *  — the whole risk of this lane is keeping builder/wizard resume
 *  untouched. */
export function basketKey(slug: string): string {
  return `pe:basket:${slug || "default"}`;
}

function defaultStorage(): StorageLike | null {
  try {
    if (typeof window !== "undefined" && window.localStorage) return window.localStorage;
  } catch {
    // Safari private mode throws on property access — treat as absent.
  }
  return null;
}

function defaultOnStorage(handler: (key: string | null) => void): () => void {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") {
    return () => { /* SSR / no window — nothing to unsubscribe */ };
  }
  const listener = (e: StorageEvent) => {
    try {
      handler(e.key);
    } catch {
      /* never let a listener throw out of the event loop */
    }
  };
  window.addEventListener("storage", listener);
  return () => {
    try {
      window.removeEventListener("storage", listener);
    } catch {
      /* ignore */
    }
  };
}

/** Coerce an untrusted item (from JSON or a caller) into a clean
 *  BasketItem. variantId is required upstream (callers/hydrate drop
 *  items without one); qty floors at 1. */
function normalizeItem(raw: any): BasketItem {
  const item: BasketItem = {
    variantId: String(raw.variantId).trim(),
    title: typeof raw.title === "string" && raw.title.trim() ? raw.title : "Product",
    qty: Math.max(1, Math.floor(Number(raw.qty)) || 1),
  };
  if (raw.productId != null && String(raw.productId).trim()) item.productId = String(raw.productId).trim();
  if (typeof raw.variantTitle === "string" && raw.variantTitle) item.variantTitle = raw.variantTitle;
  if (typeof raw.sku === "string" && raw.sku) item.sku = raw.sku;
  if (raw.price != null) item.price = typeof raw.price === "number" ? raw.price.toFixed(2) : String(raw.price);
  if (typeof raw.vendor === "string" && raw.vendor) item.vendor = raw.vendor;
  if (typeof raw.imageUrl === "string" && raw.imageUrl) item.imageUrl = raw.imageUrl;
  // Inventory awareness — preserved verbatim so badges/submit-gate survive
  // the round-trip through the store (the unified item source of truth).
  if (typeof raw.availableForSale === "boolean") item.availableForSale = raw.availableForSale;
  if (raw.inventoryPolicy === "DENY" || raw.inventoryPolicy === "CONTINUE") item.inventoryPolicy = raw.inventoryPolicy;
  if (raw.inventoryQuantity != null && isFinite(Number(raw.inventoryQuantity))) item.inventoryQuantity = Math.floor(Number(raw.inventoryQuantity));
  if (typeof raw.discontinued === "boolean") item.discontinued = raw.discontinued;
  const props = normalizeProperties(raw.properties);
  if (props.length) item.properties = props;
  return item;
}

/** Filter/clean a properties array — drop non-{name,value} entries and
 *  blank names/values so the drawer + submit payload only ever carry
 *  real custom options. */
function normalizeProperties(raw: any): BasketLineProperty[] {
  if (!Array.isArray(raw)) return [];
  const out: BasketLineProperty[] = [];
  for (const p of raw) {
    if (!p || typeof p !== "object") continue;
    const name = typeof p.name === "string" ? p.name.trim() : "";
    const value = p.value == null ? "" : String(p.value).trim();
    if (!name || !value) continue;
    out.push({ name, value });
  }
  return out;
}

/** The basket controller. One instance per page (index.ts holds a
 *  singleton). Subscribe to re-render the indicator/drawer on change;
 *  mutations persist synchronously and emit. */
export class BasketStore {
  private slug: string;
  private key: string;
  private storage: StorageLike | null;
  private now: () => number;
  private items: BasketItem[] = [];
  private listeners = new Set<(items: BasketItem[]) => void>();
  private storageOff: (() => void) | null = null;

  constructor(deps: BasketStoreDeps = {}) {
    this.slug = deps.slug || "default";
    this.key = basketKey(this.slug);
    this.storage = deps.storage !== undefined ? deps.storage : defaultStorage();
    this.now = deps.now || (() => Date.now());
    // Initial hydrate from storage (applies the 7-day TTL).
    this.items = this.readFromStorage();
    // Cross-tab sync: a second tab that adds/removes writes the key; the
    // `storage` event fires in THIS tab (never in the tab that wrote),
    // so we re-hydrate + emit to keep the indicator/drawer in step.
    const onStorage = deps.onStorage || defaultOnStorage;
    try {
      this.storageOff = onStorage((changedKey) => {
        // null key == storage.clear(); otherwise ignore unrelated keys.
        if (changedKey !== null && changedKey !== this.key) return;
        this.items = this.readFromStorage();
        this.emit();
      });
    } catch {
      // No window / hostile env — in-memory only, no cross-tab sync.
      this.storageOff = null;
    }
  }

  /** Read + validate the persisted basket. TTL-expired or corrupt data
   *  resolves to an empty basket (and prunes the stale key). */
  private readFromStorage(): BasketItem[] {
    if (!this.storage) return [];
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Partial<BasketData>;
      const ts = parsed && parsed.updatedAt ? new Date(parsed.updatedAt).getTime() : 0;
      if (!ts || this.now() - ts > BASKET_TTL_MS) {
        try { this.storage.removeItem(this.key); } catch { /* swallow */ }
        return [];
      }
      const rawItems = Array.isArray(parsed.items) ? parsed.items : [];
      const out: BasketItem[] = [];
      for (const it of rawItems) {
        if (!it || it.variantId == null || !String(it.variantId).trim()) continue;
        out.push(normalizeItem(it));
      }
      return out;
    } catch {
      // Corrupt JSON / no storage / quota — behave as empty in-memory.
      return [];
    }
  }

  private persist(): void {
    if (!this.storage) return;
    try {
      const data: BasketData = {
        version: BASKET_VERSION,
        updatedAt: new Date(this.now()).toISOString(),
        items: this.items,
      };
      this.storage.setItem(this.key, JSON.stringify(data));
    } catch {
      // Quota / private mode — the in-memory copy still drives the UI
      // for this session; it just won't survive navigation.
    }
  }

  /** Add an item. Dedup-by-variant: a 2nd add of the same variant bumps
   *  the existing line's qty (properties on the existing line are left
   *  as first set). No-op on a missing variantId. */
  add(item: BasketItem): void {
    const vid = String(item.variantId == null ? "" : item.variantId).trim();
    if (!vid) return;
    const addQty = Math.max(1, Math.floor(Number(item.qty)) || 1);
    const existing = this.items.find((i) => i.variantId === vid);
    if (existing) {
      existing.qty += addQty;
    } else {
      this.items.push(normalizeItem({ ...item, variantId: vid, qty: addQty }));
    }
    this.persist();
    this.emit();
  }

  /** Replace the entire basket with a fresh set of lines — the
   *  authoritative-seed sink (cart-page import / lineItems prefill).
   *  Normalizes + dedups within the incoming set (a repeated variant
   *  bumps qty) and persists + emits ONCE (unlike a clear()+N×add()
   *  which would emit N+1 times). An empty/nullish set clears. */
  setItems(items: readonly BasketItem[] | null | undefined): void {
    const next: BasketItem[] = [];
    const byVariant = new Map<string, BasketItem>();
    if (Array.isArray(items)) {
      for (const raw of items) {
        if (!raw || raw.variantId == null || !String(raw.variantId).trim()) continue;
        const vid = String(raw.variantId).trim();
        const existing = byVariant.get(vid);
        if (existing) {
          existing.qty += Math.max(1, Math.floor(Number(raw.qty)) || 1);
          continue;
        }
        const item = normalizeItem({ ...raw, variantId: vid });
        byVariant.set(vid, item);
        next.push(item);
      }
    }
    this.items = next;
    this.persist();
    this.emit();
  }

  /** Remove a line entirely. */
  remove(variantId: string): void {
    const vid = String(variantId == null ? "" : variantId).trim();
    const before = this.items.length;
    this.items = this.items.filter((i) => i.variantId !== vid);
    if (this.items.length !== before) {
      this.persist();
      this.emit();
    }
  }

  /** Set an absolute quantity for a line. n <= 0 removes the line. No-op
   *  when the variant isn't in the basket. */
  setQty(variantId: string, n: number): void {
    const vid = String(variantId == null ? "" : variantId).trim();
    const item = this.items.find((i) => i.variantId === vid);
    if (!item) return;
    const q = Math.floor(Number(n));
    if (!isFinite(q) || q <= 0) {
      this.remove(vid);
      return;
    }
    item.qty = q;
    this.persist();
    this.emit();
  }

  /** Defensive copy of the current lines — callers can't mutate our
   *  internal array. */
  getItems(): BasketItem[] {
    return this.items.map((i) => ({ ...i, properties: i.properties ? i.properties.map((p) => ({ ...p })) : undefined }));
  }

  /** Total units across all lines (sum of qty) — what the floating
   *  "Quote (N)" indicator shows. */
  count(): number {
    return this.items.reduce((sum, i) => sum + (i.qty || 0), 0);
  }

  /** Number of distinct lines. */
  lineCount(): number {
    return this.items.length;
  }

  /** Empty the basket AND drop the persisted key. Called on submit
   *  success (the quote request now owns those lines). */
  clear(): void {
    this.items = [];
    if (this.storage) {
      try { this.storage.removeItem(this.key); } catch { /* swallow */ }
    }
    this.emit();
  }

  /** Subscribe to change. Fires on every mutation (this tab) + on
   *  cross-tab storage sync. Returns an unsubscribe fn. */
  subscribe(fn: (items: BasketItem[]) => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private emit(): void {
    const snapshot = this.getItems();
    this.listeners.forEach((fn) => {
      try { fn(snapshot); } catch { /* one bad subscriber can't block the rest */ }
    });
  }

  /** Tear down the cross-tab listener. Rarely needed (the basket lives
   *  for the page lifetime) — provided for completeness + tests. */
  destroy(): void {
    if (this.storageOff) {
      try { this.storageOff(); } catch { /* ignore */ }
      this.storageOff = null;
    }
    this.listeners.clear();
  }
}

/** Structural shape of the form's PrefillLineItem (defined in index.ts,
 *  which the node-only test can't import). Kept in sync by the compiler:
 *  index.ts casts the mapper's output to PrefillLineItem[], so a drift in
 *  the required fields fails tsc there. */
export interface PrefillLineLike {
  variantId: string;
  productId?: string;
  qty: number;
  title?: string;
  variantTitle?: string;
  sku?: string;
  price?: string;
  vendor?: string;
  imageUrl?: string | null;
  properties?: BasketLineProperty[];
}

/** Map basket/cart line items → the form's lineItems prefill shape. Under
 *  the unified model the multi-step form seeds items straight from the
 *  basket store on mount, so this mapper's live caller is the CLASSIC
 *  (basket-off) quick-form "Open the full form" escalation, which carries
 *  the quick form's current cartItems (structurally a BasketItem superset)
 *  in-memory via prefill. Pure — no DOM, no store — so that payload is
 *  unit-testable. Skips entries with no resolvable variantId (the form
 *  can't seed a line it can't identify) and only attaches `properties`
 *  when there's at least one. */
export function basketItemsToPrefill(items: readonly BasketItem[] | null | undefined): PrefillLineLike[] {
  if (!Array.isArray(items)) return [];
  const out: PrefillLineLike[] = [];
  for (const it of items) {
    if (!it || it.variantId == null || !String(it.variantId).trim()) continue;
    const line: PrefillLineLike = {
      variantId: String(it.variantId).trim(),
      qty: Math.max(1, Math.floor(Number(it.qty)) || 1),
      imageUrl: it.imageUrl ?? null,
    };
    if (it.productId != null && String(it.productId).trim()) line.productId = String(it.productId);
    if (it.title) line.title = it.title;
    if (it.variantTitle) line.variantTitle = it.variantTitle;
    if (it.sku) line.sku = it.sku;
    if (it.price != null && String(it.price)) line.price = String(it.price);
    if (it.vendor) line.vendor = it.vendor;
    const props = normalizeProperties(it.properties);
    if (props.length) line.properties = props;
    out.push(line);
  }
  return out;
}

/** Push a list of line items into the basket store — the shared sink for
 *  the "many items at once" doors (today: the in-quick-form "Add to
 *  Quote" exit hands the quick form's current cartItems here; the store's
 *  own dedup-by-variant bumps qty on any variant already present).
 *  Skips entries with no resolvable variantId (store.add would no-op on
 *  them anyway) so the returned count reflects only lines actually
 *  handed to the store. Structurally-compatible CartItem[] flows straight
 *  in — BasketItem is a superset. Never throws (a hostile item can't
 *  break the exit). */
export function addItemsToBasket(store: Pick<BasketStore, "add">, items: readonly BasketItem[] | null | undefined): number {
  if (!store || !Array.isArray(items)) return 0;
  let added = 0;
  for (const it of items) {
    if (!it || it.variantId == null || !String(it.variantId).trim()) continue;
    try {
      store.add(it);
      added++;
    } catch {
      /* one bad item can't abort the rest of the push */
    }
  }
  return added;
}
